import React, { useEffect, useState } from "react";
import axios from "axios";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faGear, faCircleCheck, faCircleExclamation } from "@fortawesome/free-solid-svg-icons";
import "../css/SettingsPage.css";

const SETTING_KEY = "email_redirect";

const SettingsPage = () => {
  const [enabled, setEnabled] = useState(false);
  const [testEmail, setTestEmail] = useState("");
  const [saved, setSaved] = useState(false);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const API_URL = process.env.REACT_APP_API_URL;

  useEffect(() => {
    const fetchSetting = async () => {
      try {
        const res = await axios.get(`${API_URL}/settings/${SETTING_KEY}`);
        const value = res.data?.value || {};
        setEnabled(!!value.enabled);
        setTestEmail(value.testEmail || "");
      } catch (err) {
        if (err.response?.status !== 404) {
          console.error("Failed to fetch email redirect setting", err);
          setError("Failed to load settings");
        }
      } finally {
        setLoading(false);
      }
    };
    fetchSetting();
  }, [API_URL]);

  const handleSave = async () => {
    setSaving(true);
    setError("");
    try {
      await axios.put(`${API_URL}/settings/${SETTING_KEY}`, {
        value: { enabled, testEmail },
      });
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
    } catch (err) {
      console.error("Failed to save email redirect setting", err);
      setError("Failed to save settings");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="settings-page p-4">
      <div className="settings-page__header">
        <h4 className="settings-page__title">
          <FontAwesomeIcon icon={faGear} />
          Settings
        </h4>
        <p className="settings-page__subtitle">
          Configuration that applies to this environment only.
        </p>
      </div>

      <div className="settings-section">
        <div className="settings-section__header">
          <div>
            <h6 className="settings-section__heading">Email Redirect</h6>
            <p className="settings-section__desc">
              Send every outbound email to one test inbox instead of real recipients — use this while testing templates or campaigns.
            </p>
          </div>
          <span className={`settings-status-badge ${enabled ? "settings-status-badge--on" : "settings-status-badge--off"}`}>
            <span className="settings-status-badge__dot" />
            {enabled ? "Redirecting" : "Off"}
          </span>
        </div>

        {loading ? (
          <div className="text-muted p-4" style={{ fontSize: "13px" }}>Loading…</div>
        ) : (
          <>
            <div className="settings-row">
              <div>
                <p className="settings-row__label">Redirect outbound mail</p>
                <p className="settings-row__hint">All recipients are replaced with the test address below.</p>
              </div>
              <div className="settings-row__control text-end">
                <div className="form-check form-switch d-inline-block m-0">
                  <input
                    className="form-check-input settings-toggle"
                    type="checkbox"
                    role="switch"
                    id="emailRedirectEnabled"
                    checked={enabled}
                    onChange={(e) => setEnabled(e.target.checked)}
                  />
                </div>
              </div>
            </div>

            <div className="settings-row">
              <div>
                <label className="settings-row__label" htmlFor="emailRedirectTestEmail">
                  Test email address
                </label>
                <p className="settings-row__hint">Where redirected mail is delivered.</p>
              </div>
              <div className="settings-row__control">
                <input
                  id="emailRedirectTestEmail"
                  type="email"
                  className="form-control form-control-sm"
                  placeholder="test@example.com"
                  value={testEmail}
                  onChange={(e) => setTestEmail(e.target.value)}
                  disabled={!enabled}
                />
              </div>
            </div>

            {enabled && (
              <div className="mx-3 mb-3 alert alert-warning py-2 px-3" style={{ fontSize: "13px" }}>
                All outbound email currently goes to <strong>{testEmail || "the test address above"}</strong>, not real recipients.
              </div>
            )}

            <div className="settings-page__footer">
              <button
                className="btn btn-success settings-save-btn"
                onClick={handleSave}
                disabled={saving || (enabled && !testEmail)}
              >
                {saving ? "Saving…" : "Save"}
              </button>
              {saved && (
                <span className="settings-saved-pill">
                  <FontAwesomeIcon icon={faCircleCheck} />
                  Saved
                </span>
              )}
              {error && (
                <span className="settings-error-text">
                  <FontAwesomeIcon icon={faCircleExclamation} className="me-1" />
                  {error}
                </span>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default SettingsPage;
